import calibratePhoto from '../../elements/calibrate-photo/calibrate-photo.js';
import { PickAnAttribute } from '../PickAnAttribute.js';
import { ApplyEffectiveMoves } from '../ApplyEffectiveMoves.js';
import { GetClearanceRuleForMarker } from './MRClearances.js';
import { Path } from '../Path.js';

/**
 * @typedef ItemMRStateResult
 * @property {object} MRItems - The MR items for the photo
 * @property {'No MR'|'MR Resolved'|'MR Unresolved'} newMRStateValue - The new MR state value
 * @property {boolean} MRStateHasChanged - Whether the MR state has changed
 * @property {object|null} computedGroundMarker - The computed ground marker
 * @property {boolean} groundMarkerHasChanged - Whether the ground marker has changed
 */

/**
 * Computes the photo lookups for the item and photo data.
 * @param {object} associatedItem - The item associated with the photo
 * @param {object} photo - The photo we are computing lookups for
 * @param {string} photoId - The photo id
 * @param {object} supplementalData - Additional data to complete the calculations
 * @param {object} supplementalData.traces - The traces for the job
 * @param {object} supplementalData.makeReadyRules - The make ready rules for the job model
 * @param {object} [options] - Additional options for the calculations
 * @param {object} [options.effectiveMovesDistanceRatios] - The effective moves distance ratios
 * @returns {{MRItems: object, itemLookups: object}} The MR items and item lookups
 */
export function computePhotoLookups(associatedItem, photo, photoId, supplementalData, options = {}) {
  const { traces, makeReadyRules } = supplementalData;
  const { effectiveMovesDistanceRatios } = options;

  // Initialize the lookups
  const MRItems = {};
  const itemLookups = {};

  // Make sure the photo is on the item and the photo has photofirst data
  const associatedItemHasPhoto = associatedItem?.photos?.[photoId] != null;
  const photoDataHasPhotofirstData = photo?.photofirst_data != null;
  if (!associatedItemHasPhoto || !photoDataHasPhotofirstData) return { MRItems, itemLookups };

  // Make the make ready rules into a list
  const orderedMakeReadyRulesList = Object.entries(makeReadyRules ?? {})
    .map(([$key, value]) => ({ $key, ...value }))
    .sort((a, b) => (a.order ?? null) - (b.order ?? null));

  // Loop through all the markers in the photo data
  for (const inputModelKey in photo.photofirst_data) {
    for (const markerKey in photo.photofirst_data[inputModelKey]) {
      const marker = photo.photofirst_data[inputModelKey][markerKey];
      const height = marker?._measured_height || marker?._manual_height;

      if (height == null) continue;

      // Round the height so the moves match the displayed (UI) heights
      const roundedHeight = Math.round(height);
      const parsedMRMove = parseInt(marker.mr_move) || 0;
      const { effectiveMoveAmount } = ApplyEffectiveMoves(marker, null, { ratios: effectiveMovesDistanceRatios });
      const move = parsedMRMove + effectiveMoveAmount;

      const markerInfo = { marker, property: inputModelKey, markerKey, height: roundedHeight, move };
      const topMarkerInfo = { parentKey: markerKey, parentMarker: marker };

      // Apply the appropriate make ready model to the marker
      setMakeReadyModelOnLookups(MRItems, itemLookups, markerInfo, topMarkerInfo, { traces, orderedMakeReadyRulesList });

      // Apply the appropriate make ready model to all children of the marker
      runOnAllMarkerChildren(marker, (childMarker, childPath, inputModelKey, childMarkerKey) => {
        const childMarkerInfo = { marker: childMarker, property: inputModelKey, markerKey: childMarkerKey, height: roundedHeight, move };
        setMakeReadyModelOnLookups(MRItems, itemLookups, childMarkerInfo, topMarkerInfo, { traces, orderedMakeReadyRulesList });
      });
    }
  }

  return { MRItems, itemLookups };
}

/**
 * Gets the computed ground marker for the photo data.
 * @param {object} photoData - The photo data from the photo
 * @returns {{computedGroundMarker: object, groundMarkerHasChanged: boolean}} The computed ground marker and whether it has changed.
 */
function getComputedGroundMarker(photoData) {
  // Get the zero percent pixel from the stick align data (or use default values)
  const zeroPercentPixel = photoData.stick_align
    ? getZeroPercentPixelFromStickAlign(photoData.stick_align)
    : // Default to the "zero" pixel location for the blank midspan photo
      { percentX: 49.504, percentY: 85.3578 };

  // Get the existing ground marker data
  const existingGroundMarker = photoData?.photofirst_data?.ground_marker?.auto_added || {};
  // Make a copy of the existing marker to apply data to
  const computedGroundMarker = structuredClone(existingGroundMarker);

  // Set the height and pixel selection back to (almost) 0
  computedGroundMarker._measured_height = 0.01;
  computedGroundMarker.pixel_selection = [zeroPercentPixel];

  // Add any midspan properties to the ground marker
  const midspanHeight = photoData?.photofirst_data?.midspanHeight || {};
  const firstMidspanChip = Object.values(midspanHeight)[0] || {};
  // Add the midspan properties to the new ground marker
  for (const [attributeKey, value] of Object.entries(firstMidspanChip)) computedGroundMarker[attributeKey] = value;

  // Save important properties so they are not deleted
  const preservedGroundMarkerProperties = ['_measured_height', 'pixel_selection', 'mr_note', 'mr_move', 'mr_ignore', 'mr_resolved'];
  // Remove any properties that are not in the groundMarkerSave list or in the midspanChip
  for (const attributeKey in computedGroundMarker) {
    if (!preservedGroundMarkerProperties.includes(attributeKey) && firstMidspanChip[attributeKey] == null) {
      delete computedGroundMarker[attributeKey];
    }
  }

  // Check if the new marker differs from the existing marker
  const groundMarkerHasChanged = deepEqual(existingGroundMarker, computedGroundMarker);

  return { computedGroundMarker, groundMarkerHasChanged };
}

/**
 * Gets the zero percent pixel from the stick align data.
 * @param {object} stickAlign - The stick align data
 * @returns {{percentX: number, percentY: number}} The zero percent pixel
 */
function getZeroPercentPixelFromStickAlign(stickAlign) {
  const zeroPixel = calibratePhoto.getPixel(0, stickAlign);
  return { percentX: (zeroPixel.x / stickAlign.width) * 100, percentY: (zeroPixel.y / stickAlign.height) * 100 };
}

function setMakeReadyModelOnLookups(MRItems, itemLookups, markerInfo, parentInfo, supplementalData) {
  const { marker, property, markerKey, height, move } = markerInfo;
  const { parentKey, parentMarker } = parentInfo;
  const { traces, orderedMakeReadyRulesList } = supplementalData;

  const traceData = traces?.[marker._trace];
  const matchingModel = GetClearanceRuleForMarker(marker, property, orderedMakeReadyRulesList, traces) || { $key: 'none' };

  const isArmMarker = property == 'arm' && marker.arm_spec != null;
  const proposedMR = isArmMarker && marker.proposed && marker.arm_spec.toLowerCase().includes('guard');

  MRItems[markerKey] = {
    modelKey: matchingModel.$key,
    shorthand: matchingModel.shorthand,
    exceptions: matchingModel.exceptions,
    manual_resolution: marker.mr_resolved || parentMarker.mr_resolved,
    mr_ignore: marker.mr_ignore || parentMarker.mr_ignore,
    mr_remove: marker.mr_remove || parentMarker.mr_remove,
    mr_note: marker.mr_note || parentMarker.mr_note,
    proposed_mr: proposedMR,
    parentKey,
    property,
    proposed: marker.proposed || traceData?.proposed,
    height,
    _trace: marker._trace,
    move,
    movedHeight: height + move,
    preMrViolation: false,
    violations: {},
    violationList: [],
    preMrViolationList: [],
    violation: false
  };

  itemLookups[matchingModel.$key] = itemLookups[matchingModel.$key] || {};
  itemLookups[matchingModel.$key][markerKey] = true;
}

/**
 * Calculates the photo clearances and MR state for a job item.
 * @param {object} item - The item from the job
 * @param {object} photo - The photo we are calculating clearances for
 * @param {string} photoId - The photo id
 * @param {string} photoAssociation - The association type of the photo
 * @param {object} supplementalData - Additional data to complete the calculations
 * @param {object} supplementalData.traces - The traces for the job
 * @param {object} supplementalData.makeReadyRules - The make ready rules for the job model
 * @param {object} [options] - Additional options for the calculations
 * @param {object} [options.effectiveMovesDistanceRatios] - The effective moves distance ratios
 * @param {boolean} [options.autoInsertGroundMarkerInPolePhoto] - Whether to auto insert ground markers in pole photos
 * @param {boolean} [options.includeProposedInPreMrViolations] - Whether to include proposed in pre-MR violations
 * @returns {ItemMRStateResult} The MR items, new MR state value, and whether the MR state has changed
 */
export function calcPhotoClearances(item, photo, photoId, photoAssociation, supplementalData, options = {}) {
  const { traces, makeReadyRules } = supplementalData;
  const { effectiveMovesDistanceRatios, autoInsertGroundMarkerInPolePhoto, includeProposedInPreMrViolations } = options;

  // Check if the photo is associated with a section, connection, or if we have a model option to insert ground markers in pole photos
  const shouldCheckGroundMarker =
    useMidspanClearances(photoAssociation, item) || (photoAssociation == 'node' && autoInsertGroundMarkerInPolePhoto);
  // Get the computed ground marker and check if it has changed
  const { computedGroundMarker = null, groundMarkerHasChanged = false } = shouldCheckGroundMarker ? getComputedGroundMarker(photo) : {};
  // Update the ground marker in the photo data if it has changed
  if (groundMarkerHasChanged) photo.photofirst_data.ground_marker = { auto_added: computedGroundMarker };

  const { MRItems, itemLookups } = computePhotoLookups(item, photo, photoId, { traces, makeReadyRules }, { effectiveMovesDistanceRatios });

  const photoResults = { hasViolations: false, hasPreMrViolations: false, resolvedMr: false };
  for (const markerKey in MRItems) {
    const results = calcViolations(MRItems, itemLookups, item, markerKey, photoAssociation, makeReadyRules, {
      includeProposedInPreMrViolations
    });
    if (!results) continue;

    // Combine the results from all markers
    photoResults.hasViolations ||= results.hasViolations;
    photoResults.hasPreMrViolations ||= results.hasPreMrViolations;
    photoResults.resolvedMr ||= results.resolvedMr;
  }

  // Get the current MR state value and MR note value
  const attributes = item.attributes || item.multi_attributes;
  const currentMRStateValue = PickAnAttribute(attributes, 'mr_state');
  const currentMRNoteValue = PickAnAttribute(attributes, 'mr_note') || '';

  // Get the new MR state value and check if it has changed
  const newMRStateValue = getMRStateValue(photoResults, currentMRNoteValue);
  const MRStateHasChanged = newMRStateValue !== currentMRStateValue;

  return { MRItems, newMRStateValue, MRStateHasChanged, computedGroundMarker, groundMarkerHasChanged };
}

/**
 * @param {{ hasViolations: boolean, hasPreMrViolations: boolean, resolvedMr: boolean}} photoResults
 * @param {string} MRNoteValue - The value of the MR note attribute from the item
 * @returns {ItemMRStateResult['newMRStateValue']}
 */
function getMRStateValue(photoResults, MRNoteValue) {
  if (photoResults.hasViolations) return 'MR Unresolved';
  if (photoResults.hasPreMrViolations || photoResults.resolvedMr || MRNoteValue) return 'MR Resolved';
  return 'No MR';
}

function useMidspanClearances(photoAssociation, associatedItem) {
  return (
    photoAssociation == 'section' ||
    photoAssociation == 'connection' ||
    PickAnAttribute(associatedItem?.attributes, 'node_type') == 'crossover'
  );
}

function calcViolations(MRItems, itemLookups, item, markerKey, photoAssociation, makeReadyRules, options = {}) {
  const { includeProposedInPreMrViolations } = options;

  if (MRItems[markerKey].mr_ignore) return;

  const clearanceKey = useMidspanClearances(photoAssociation, item) ? 'midspan_clearances' : 'clearances';
  const clearances = makeReadyRules?.[MRItems[markerKey].modelKey]?.[clearanceKey];

  let hasViolations = false;
  let hasPreMrViolations = false;
  let violationList = [];
  let preMrViolationList = [];

  for (var id in clearances) {
    for (var iKey in itemLookups[id]) {
      if (iKey != markerKey && MRItems[iKey].parentKey != MRItems[markerKey].parentKey) {
        let existingViolation = isViolation(
          MRItems[markerKey].height,
          MRItems[iKey].height,
          clearances[id].clearance,
          clearances[id].direction
        );
        let newViolation = isViolation(
          MRItems[markerKey].movedHeight,
          MRItems[iKey].movedHeight,
          clearances[id].clearance,
          clearances[id].direction
        );

        // If the ignore_existing_violations is set to true, then we should ignore this
        // new violation if there is no mr move (because that means the proposed height is
        // still the same as the existing height, and existing violations should be ignored)
        if (
          clearances[id].ignore_existing_violations == true &&
          !MRItems[markerKey].move &&
          !MRItems[markerKey].proposed &&
          !MRItems[iKey].proposed
        ) {
          newViolation = false;
        }

        if (MRItems[markerKey].mr_remove || MRItems[iKey].mr_remove) {
          newViolation = false;
        }

        if (existingViolation && !clearances[id].ignore_existing_violations) {
          // Check if a proposed attachment is involved in the violation (either attachment is proposed)
          let proposedInvolved = MRItems[markerKey].proposed == true || MRItems[iKey].proposed == true;
          // Only add the violation to the preMrViolationList if either no proposed is involved, or if
          // a proposed is involved, then it should be allowed with the includeProposedInPreMrViolations option
          if (!proposedInvolved || (proposedInvolved && includeProposedInPreMrViolations)) {
            hasPreMrViolations = true;
            preMrViolationList.push({
              direction: clearances[id].direction,
              clearance: clearances[id].clearance,
              itemKey: iKey,
              height: MRItems[iKey].height,
              shorthand: MRItems[iKey].shorthand,
              _trace: MRItems[iKey]._trace
            });
          }
        }
        if (newViolation) {
          hasViolations = true;
          violationList.push({
            direction: clearances[id].direction,
            clearance: clearances[id].clearance,
            itemKey: iKey,
            height: MRItems[iKey].height,
            shorthand: MRItems[iKey].shorthand,
            _trace: MRItems[iKey]._trace
          });
        }
        // Set the violation state on the item's own violation list and the other item's violation list
        Path.set(MRItems, `${markerKey}.violations.${iKey}`, newViolation);
        // Only set the newViolation state on the other marker if there is not a value set already
        // This is to prevent the issue where item A is in violation and item B is not
        // because it has not moved and has been marked to ignore existing violations.
        // If item B is ignoring violations and is processed after item A, then item
        // B would override its key in item A's violations list. So check to make sure it's
        // not set yet by either marker before setting it
        if (MRItems?.[iKey]?.violations?.[markerKey] == null) {
          Path.set(MRItems, `${iKey}.violations.${markerKey}`, newViolation);
        }
      }
    }
  }
  if (MRItems[markerKey].manual_resolution) {
    hasViolations = false;
  }

  const resolvedMr =
    MRItems[markerKey].proposed_mr ||
    MRItems[markerKey].manual_resolution ||
    MRItems[markerKey].mr_note ||
    MRItems[markerKey].mr_remove ||
    (hasViolations == false && MRItems[markerKey].height != MRItems[markerKey].movedHeight);

  Path.set(MRItems, `${markerKey}.violation`, hasViolations);
  Path.set(MRItems, `${markerKey}.preMrViolation`, hasPreMrViolations);
  Path.set(MRItems, `${markerKey}.violationList`, violationList);
  Path.set(MRItems, `${markerKey}.preMrViolationList`, preMrViolationList);

  return { hasViolations, hasPreMrViolations, resolvedMr };
}

function isViolation(a, b, clearance, direction) {
  if (direction == 'above') return a - clearance < b;
  else if (direction == 'below') return a + clearance > b;
  else if (direction == 'away from') return (a >= b && a - clearance < b) || (a <= b && a + clearance > b);
}

/**
 * Runs a function on all children of a parent object recursively.
 * @param {any} parentMarker - The parent object to run on.
 * @param {(
 *   childMarker: any,
 *   childPath: string,
 *   inputModelKey: string,
 *   childMarkerKey: string
 * ) => void} childFunction - The function to call on each child.
 * @param {string} [path] - The current path in the object.
 */
function runOnAllMarkerChildren(parentMarker, childFunction, path = '') {
  for (const inputModelKey in parentMarker._children) {
    for (const childMarkerKey in parentMarker._children[inputModelKey]) {
      const childPath = `${path}/_children/${inputModelKey}/${childMarkerKey}`;
      const childMarker = parentMarker._children[inputModelKey][childMarkerKey];
      childFunction(childMarker, childPath, inputModelKey, childMarkerKey);
      runOnAllMarkerChildren(childMarker, childFunction, childPath);
    }
  }
}

/**
 * Deeply compares two values for equality. Objects are considered equal if they have the same keys and values (though the order of the keys
 * does not matter). Arrays are considered equal if they have the same length and elements in the same order.
 * @param {any} a - The first object to compare
 * @param {any} b - The second object to compare
 * @returns {boolean} True if the objects are deeply equal, false otherwise
 */
function deepEqual(a, b) {
  // If both are non-null objects, check for equality
  if (a !== null && b !== null && typeof a === 'object' && typeof b === 'object') {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);

    // Check if the number of keys is the same
    if (aKeys.length !== bKeys.length) return false;

    // Check if all keys in a exist in b and are equal
    for (const key of aKeys) {
      if (!bKeys.includes(key) || !deepEqual(a[key], b[key])) return false;
    }

    return true;
  }

  // For non-object values, check for strict equality
  return a === b;
}
